import React, { useEffect, useState } from 'react';
import StarIcon from './StarIcon';

interface StarRatingProps {
  stars: number; // 0-3
  animate?: boolean;
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showOnlyFilled?: boolean;
  useSvg?: boolean;
}

const StarRating: React.FC<StarRatingProps> = ({ stars, animate = false, size = 'medium', showOnlyFilled = false, useSvg = false }) => {
  const [visibleStars, setVisibleStars] = useState(animate ? 0 : stars);

  useEffect(() => {
    if (animate && stars > 0) {
      // Animate stars appearing one by one
      let currentStar = 0;
      const interval = setInterval(() => {
        currentStar++;
        setVisibleStars(currentStar);

        if (currentStar >= stars) {
          clearInterval(interval);
        }
      }, 100); // 100ms between each star

      return () => clearInterval(interval);
    }
  }, [stars, animate]);

  const maxStars = showOnlyFilled ? stars : 3;

  // Map size to pixel dimensions
  const sizeMap = {
    small: 20,
    medium: 32,
    large: 48,
    xlarge: 96
  };

  const starSize = sizeMap[size];

  return (
    <div className={`star-rating ${size}`}>
      {Array.from({ length: maxStars }, (_, index) => {
        const starNumber = index + 1;
        const isFilled = showOnlyFilled ? true : starNumber <= visibleStars;
        const isEarned = starNumber <= stars;
        const willBeEarned = animate && starNumber <= stars;
        const notYetVisible = animate && !isFilled && willBeEarned;
        const isUnearned = !isEarned;
        const unearnedNotYetShown = animate && isUnearned && visibleStars < stars;

        // Apply animation to filled stars when animate is true
        const shouldAnimate = animate && isFilled && isEarned;
        // Animate unfilled stars with a fade-in after all filled stars are done
        const shouldFadeIn = animate && isUnearned && visibleStars >= stars;

        return (
          <div
            key={index}
            className={`star ${isFilled ? 'visible' : 'empty'} ${shouldAnimate ? 'pop-in' : ''} ${shouldFadeIn ? 'fade-in' : ''} ${(notYetVisible || unearnedNotYetShown) ? 'hidden-before-animation' : ''}`}
          >
            <StarIcon filled={isFilled} size={starSize} useSvg={useSvg} />
          </div>
        );
      })}
    </div>
  );
};

export default StarRating;
